/**
 * Pure helpers for Automation Studio "Meta info" / run-info panel.
 * @see docs/automation-studio-meta-info-tab.md
 *
 * Matches recorder / correlation payloads (e.g. nlp_automation_phase2):
 * - **`dynamicIntentMap`**: `{ [stepUuid]: "TYPE: one-line intent" }` — used for Dynamic intent (split on first `:`).
 * - **`dynamicBindings`**: `{ [stepUuid]: Binding[] }` — used with `getDynamicBindingsForStep` in `utils/common.js`
 *   (same keys as `dynamicIntentMap`; bindings resolved via `resolveDynamicStepIdentity` + `candidateKeys`).
 * Keys are often UUIDs after placeholder rewrite; numeric `stepIndex` is still tried as a fallback key.
 */

const nonEmpty = (v) => v !== undefined && v !== null && String(v).trim() !== '';

/**
 * Split "TYPE: intent text" on the first colon (trimmed segments).
 * @param {string|null|undefined} rawString
 * @returns {{ type: string, intent: string }}
 */
export function parseDynamicIntentLabel(rawString) {
    if (!nonEmpty(rawString)) {
        return { type: '', intent: '' };
    }
    const s = String(rawString).trim();
    const idx = s.indexOf(':');
    if (idx === -1) {
        return { type: '', intent: s };
    }
    return {
        type: s.slice(0, idx).trim(),
        intent: s.slice(idx + 1).trim(),
    };
}

/** @param {unknown} v */
function normalizeIntentEntryValue(v) {
    if (v == null) return null;
    if (typeof v === 'string') {
        const t = v.trim();
        return t || null;
    }
    if (typeof v === 'object') {
        const t = v.intent ?? v.text ?? v.label;
        return nonEmpty(t) ? String(t).trim() : null;
    }
    return null;
}

/**
 * Keys to try against `dynamicIntentMap` (id + numeric index as string).
 * @param {string|number|null|undefined} stepId
 * @param {string|number|null|undefined} stepIndex
 * @returns {string[]}
 */
export function buildDynamicIntentLookupKeys(stepId, stepIndex) {
    /** @type {string[]} */
    const keys = [];
    const add = (v) => {
        if (v === undefined || v === null || v === '') return;
        const s = String(v).trim();
        if (!s) return;
        if (!keys.includes(s)) keys.push(s);
    };
    add(stepId);
    if (stepIndex !== undefined && stepIndex !== null && String(stepIndex).trim() !== '') {
        const n = Number(stepIndex);
        if (Number.isFinite(n)) {
            add(String(n));
        }
    }
    return keys;
}

/**
 * Read first non-empty intent string from one or more map-like objects.
 * @param {Record<string, unknown>|null|undefined} map
 * @param {string[]} keys
 */
function readIntentFromMap(map, keys) {
    if (!map || typeof map !== 'object' || Array.isArray(map)) return null;
    for (const key of keys) {
        if (key === '' || key == null) continue;
        let raw = map[key];
        if (raw === undefined) {
            const num = Number(key);
            if (Number.isFinite(num)) raw = map[num];
        }
        const resolved = normalizeIntentEntryValue(raw);
        if (resolved) return resolved;
    }
    return null;
}

/**
 * All object roots that may carry `dynamicIntentMap` (FE / WS / builder shapes differ).
 * @param {object|null|undefined} dynamicResult
 * @returns {object[]}
 */
function collectIntentMapRoots(dynamicResult) {
    if (!dynamicResult || typeof dynamicResult !== 'object') return [];
    const dr = dynamicResult;
    /** @type {object[]} */
    const roots = [dr];
    if (dr.data && typeof dr.data === 'object') roots.push(dr.data);
    if (dr.metadata && typeof dr.metadata === 'object') roots.push(dr.metadata);
    if (dr.dynamicReplay && typeof dr.dynamicReplay === 'object') roots.push(dr.dynamicReplay);
    return roots;
}

/** @param {object} root */
function intentMapsFromRoot(root) {
    return [
        root.dynamicIntentMap,
        root.dynamic_intent_map,
    ].filter((m) => m && typeof m === 'object' && !Array.isArray(m));
}

/**
 * Resolve raw dynamic intent string for a step from `dynamicResult` and/or plan fields.
 *
 * Uses the same key candidates as `resolveDynamicStepIdentity` when you pass `extraCandidateKeys`
 * (e.g. `step.stepId` vs `step.id` vs flat `stepIndex`) — `dynamicIntentMap` keys match `dynamicBindings`.
 *
 * Checks:
 * - `dynamicIntentMap` on `dynamicResult`, `dynamicResult.data`, `metadata`, `dynamicReplay`
 * - legacy `dynamicIntent` / `dynamic_intent`
 *
 * @param {object|null|undefined} dynamicResult
 * @param {string|number|null|undefined} stepId
 * @param {string|number|null|undefined} [stepIndex]
 * @param {string[]|null|undefined} [extraCandidateKeys] — merged with id/index (e.g. `identity.candidateKeys`)
 * @returns {string|null}
 */
export function resolveDynamicIntentRaw(dynamicResult, stepId, stepIndex = undefined, extraCandidateKeys = undefined) {
    if (!dynamicResult) return null;

    const base = buildDynamicIntentLookupKeys(stepId, stepIndex);
    const extra = Array.isArray(extraCandidateKeys)
        ? extraCandidateKeys.map((k) => String(k).trim()).filter(Boolean)
        : [];
    const keys = [...new Set([...base, ...extra])];
    if (!keys.length) return null;

    const mapSources = [];
    for (const root of collectIntentMapRoots(dynamicResult)) {
        for (const m of intentMapsFromRoot(root)) {
            mapSources.push(m);
        }
    }

    for (const map of mapSources) {
        const hit = readIntentFromMap(map, keys);
        if (hit) return hit;
    }

    for (const root of collectIntentMapRoots(dynamicResult)) {
        const di = root.dynamicIntent ?? root.dynamic_intent;
        if (di == null) continue;

        if (typeof di === 'string') {
            const t = di.trim();
            if (t) return t;
        }

        if (Array.isArray(di)) {
            const row = di.find((x) => {
                const rowKey = String(x?.stepId ?? x?.id ?? x?.uid ?? x?.stepIndex ?? '');
                return keys.some((k) => k === rowKey);
            });
            if (row) {
                const out = normalizeIntentEntryValue(row.intent ?? row.text ?? row.label ?? row);
                if (out) return out;
            }
        }

        if (typeof di === 'object' && !Array.isArray(di)) {
            const hit = readIntentFromMap(di, keys);
            if (hit) return hit;
        }
    }

    return null;
}

/**
 * Row key for grouping: prefer sourceKey, then outputKey, then fieldPath.
 * @param {object} b
 */
function bindingRowKey(b) {
    if (nonEmpty(b?.sourceKey)) return String(b.sourceKey).trim();
    if (nonEmpty(b?.outputKey)) return String(b.outputKey).trim();
    if (nonEmpty(b?.source_key)) return String(b.source_key).trim();
    if (nonEmpty(b?.output_key)) return String(b.output_key).trim();
    if (nonEmpty(b?.fieldPath)) return String(b.fieldPath).trim();
    if (nonEmpty(b?.field_path)) return String(b.field_path).trim();
    return '';
}

/**
 * One row per unique row key. Merge: keep first non-empty placeholder; bindingType from first row
 * with a non-empty bindingType, else last seen.
 *
 * @param {object[]|null|undefined} bindingsArray
 * @returns {{ rowKey: string, placeholder: string, bindingType: string }[]}
 */
export function buildUniqueSourceKeyBindingRows(bindingsArray) {
    if (!Array.isArray(bindingsArray) || bindingsArray.length === 0) return [];

    /** @type {Map<string, { rowKey: string, placeholder: string, bindingType: string }>} */
    const map = new Map();

    for (const b of bindingsArray) {
        const rowKey = bindingRowKey(b);
        if (!rowKey) continue;

        const placeholder = nonEmpty(b?.placeholder) ? String(b.placeholder).trim() : '';
        const bindingTypeRaw = b?.bindingType ?? b?.binding_type;
        const bindingType = nonEmpty(bindingTypeRaw) ? String(bindingTypeRaw).trim() : '';

        const existing = map.get(rowKey);
        if (!existing) {
            map.set(rowKey, {
                rowKey,
                placeholder,
                bindingType,
            });
            continue;
        }
        if (!existing.placeholder && placeholder) {
            existing.placeholder = placeholder;
        }
        if (!existing.bindingType && bindingType) {
            existing.bindingType = bindingType;
        }
    }

    return Array.from(map.values());
}

/**
 * Unique token labels for "Intent tokens detected": `sourceKey` and `outputKey`
 * (generated bindings use `outputKey` only). First-seen order, de-duplicated.
 *
 * @param {object[]|null|undefined} bindingsArray
 * @returns {string[]}
 */
export function uniqueIntentTokensFromBindings(bindingsArray) {
    if (!Array.isArray(bindingsArray) || bindingsArray.length === 0) return [];
    const out = [];
    const seen = new Set();
    const tryPush = (raw) => {
        if (!nonEmpty(raw)) return;
        const k = String(raw).trim();
        if (seen.has(k)) return;
        seen.add(k);
        out.push(k);
    };
    for (const b of bindingsArray) {
        tryPush(b?.sourceKey ?? b?.source_key);
        tryPush(b?.outputKey ?? b?.output_key);
    }
    return out;
}

/**
 * Unique `sourceKey` values in first-seen order (legacy / strict source-key list).
 * @param {object[]|null|undefined} bindingsArray
 * @returns {string[]}
 */
export function uniqueSourceKeysFromBindings(bindingsArray) {
    if (!Array.isArray(bindingsArray) || bindingsArray.length === 0) return [];
    const out = [];
    const seen = new Set();
    for (const b of bindingsArray) {
        const raw = b?.sourceKey ?? b?.source_key;
        if (!nonEmpty(raw)) continue;
        const k = String(raw).trim();
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(k);
    }
    return out;
}
